import React, { useEffect } from 'react'
import { SparklesIcon } from 'lucide-react'
import Markdown from 'markdown-to-jsx'
import { useTranslation } from 'react-i18next'

import { ampli } from '@/shared/lib/ampli'

import { useAssistantConversation } from '../lib/useAssistantConversation'
import ChatMessage from './ChatMessage'

export default function ChatMessages({
	refs,
}: {
	refs: { messagesListRef: React.RefObject<HTMLDivElement | null> }
}) {
	const { t } = useTranslation()

	const { agent, messages } = useAssistantConversation()
	useEffect(() => {
		refs.messagesListRef?.current?.scrollTo({
			top: refs.messagesListRef?.current.scrollHeight,
			behavior: 'smooth',
		})
	}, [messages, refs])
	if (!agent)
		return (
			<div className='flex w-full flex-col items-center justify-center'>
				<div className='flex w-full max-w-3xl flex-col items-center justify-center gap-2 py-10'>
					<h4>
						{t('app.assistant.no_agent', { defaultValue: 'No agent selected' })}
					</h4>
				</div>
			</div>
		)
	const { welcome_text, suggestions } = agent

	return (
		<>
			<div className='flex w-full shrink-0 grow basis-0 flex-col items-start justify-end overflow-hidden'>
				<div
					className='flex w-full flex-col gap-4 overflow-auto pb-8'
					ref={refs.messagesListRef}
				>
					<div
						className='group mx-auto flex w-full max-w-3xl items-start gap-2 px-3'
						data-testid='chat-bot-welcome-text'
					>
						<div className='flex size-8 shrink-0 grow-0 items-center justify-center rounded-lg bg-primary-100 text-primary dark:bg-primary-900/20'>
							<SparklesIcon className='size-4' />
						</div>
						<div className='flex flex-col items-start gap-2 rounded-lg py-1 text-sm font-medium'>
							<span className='markdown-txt shrink-0 grow basis-0 whitespace-pre-wrap'>
								<Markdown>{welcome_text}</Markdown>
							</span>
						</div>
					</div>
					{messages.map((message) => (
						<ChatMessage key={message.id} message={message} />
					))}
				</div>
			</div>
			{!messages?.length && suggestions?.length && (
				<div className='mx-auto flex w-full max-w-3xl flex-col items-end gap-2 p-2'>
					<span className='text-sm text-muted-foreground'>
						{t('ai.assistant.suggestions_title', {
							defaultValue: 'Select from popular questions:',
						})}
					</span>
					{suggestions.map((suggestion) => (
						<ChatSuggestion key={suggestion} text={suggestion} />
					))}
				</div>
			)}
		</>
	)
}

function ChatSuggestion({ text }: { text: string }) {
	const { sendMessage } = useAssistantConversation()

	return (
		<div
			data-testid='chat-suggestion'
			onClick={() => {
				ampli.aiChatClickedSuggestion({ suggestion: text })
				sendMessage(text)
			}}
			className='cursor-pointer rounded-lg bg-slate-700/5 p-2 text-xs font-semibold hover:bg-primary-100 hover:text-primary dark:bg-white/5'
		>
			{text}
		</div>
	)
}
